import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AppChip from "../../common/app-chip";
import ButtonOne from "../../common/normal-button";
import FileDropzone from "../../common/file-dropzone";
import StatTile from "../../common/stat-tile";
import typography from "../../cssUtils/typography";
import { ColorsConstants, borderColors, grayFontColor } from "../../cssUtils/constants";
import { MEMBERS_IMPORT_ENDPOINT, MEMBERS_IMPORT_TEMPLATE_ENDPOINT } from "../../config/urls";
import { ApiError, messageFrom } from "./membersApi";
import { MAX_UPLOAD_BYTES, UPLOAD_EXTENSIONS } from "./membersUtils";

const WAIT_LIMIT_MS = 5 * 60 * 1000;
const ACCEPT = ".csv,.xlsx,.xls";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Upload member file, in 2 steps (spec 2.2): choose a file, then preview and import.
 * The file is validated in the background (a short wait), then nothing is written until Import.
 */
const UploadMembersModal = ({ api, onClose, onDone, pollMs = 1500 }) => {
  const [step, setStep] = useState(1);
  const [phase, setPhase] = useState("idle"); // idle | uploading | checking | ready | importing
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [job, setJob] = useState(null);
  const [importError, setImportError] = useState("");
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false; // stops any polling when the modal closes
    };
  }, []);

  /** Poll a job until `isFinished(job)`; resolves with the job, or null if the modal closed. */
  const waitFor = async (jobId, isFinished) => {
    const started = Date.now();
    while (alive.current) {
      const current = await api.request(`${MEMBERS_IMPORT_ENDPOINT}${jobId}/`);
      if (isFinished(current)) return current;
      if (Date.now() - started > WAIT_LIMIT_MS) throw new Error("timeout");
      await sleep(pollMs);
    }
    return null;
  };

  const reset = (message = "") => {
    setPhase("idle");
    setFile(null);
    setJob(null);
    setFileError(message);
  };

  const handleFile = async (chosen) => {
    const extension = chosen.name.split(".").pop().toLowerCase();
    if (!UPLOAD_EXTENSIONS.includes(extension)) {
      setFileError("Only CSV or Excel (.xlsx, .xls) files are supported.");
      return;
    }
    if (chosen.size > MAX_UPLOAD_BYTES) {
      setFileError("File is larger than 10 MB.");
      return;
    }
    setFile(chosen);
    setFileError("");
    setPhase("uploading");
    try {
      const created = await api.upload(MEMBERS_IMPORT_ENDPOINT, chosen);
      if (!alive.current) return;
      setPhase("checking");
      const checked = await waitFor(created.id, (current) => current.status !== "validating");
      if (!checked || !alive.current) return;
      if (checked.status === "ready") {
        setJob(checked);
        setStep(2);
        setPhase("ready");
      } else {
        reset(checked.preview?.file_error?.message || checked.failure_reason || "We couldn't read this file.");
      }
    } catch (error) {
      if (!alive.current) return;
      if (error instanceof ApiError && error.status === 400 && error.data.file) {
        reset(String(error.data.file[0]));
      } else if (error.message === "timeout") {
        reset("This is taking longer than expected. Please try again.");
      } else {
        reset(messageFrom(error, "We couldn't upload this file. Please try again."));
      }
    }
  };

  const handleImport = async () => {
    setImportError("");
    setPhase("importing");
    try {
      await api.request(`${MEMBERS_IMPORT_ENDPOINT}${job.id}/confirm/`, { method: "POST" });
      const finished = await waitFor(job.id, (current) => ["completed", "failed"].includes(current.status));
      if (!finished || !alive.current) return;
      if (finished.status === "completed") {
        onDone(finished);
      } else {
        setJob(finished);
        setImportError(finished.failure_reason || "The import did not finish.");
        setPhase("ready");
      }
    } catch (error) {
      if (!alive.current) return;
      setImportError(
        error instanceof ApiError && error.status === 409
          ? "This file was already imported or is no longer ready. Choose the file again."
          : error.message === "timeout"
            ? "The import is taking longer than expected. Check the Members list in a moment."
            : messageFrom(error),
      );
      setPhase("ready");
    }
  };

  const downloadTemplate = (format) =>
    api.download(`${MEMBERS_IMPORT_TEMPLATE_ENDPOINT}?format=${format}`, `members_template.${format}`).catch(() => {
      setFileError("We couldn't download the template. Please try again.");
    });

  const downloadReport = () =>
    api.download(`${MEMBERS_IMPORT_ENDPOINT}${job.id}/errors.csv`, "member_import_errors.csv").catch(() => {
      setImportError("We couldn't download the report. Please try again.");
    });

  const working = phase === "uploading" || phase === "checking";
  const importing = phase === "importing";
  const counts = job?.preview?.counts || {};
  const toImport = (counts.new || 0) + (counts.update || 0);

  return (
    <Dialog open onClose={importing ? undefined : onClose} fullWidth maxWidth="md" aria-labelledby="upload-title">
      <DialogTitle id="upload-title" sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography component="span" sx={{ ...typography.h2, display: "block" }}>
            Upload member file
          </Typography>
          <Typography component="span" sx={{ ...typography.bodyLarge, color: grayFontColor }}>
            {step === 1 ? "Step 1 of 2 · Choose a file" : "Step 2 of 2 · Preview and import"}
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={onClose} disabled={importing}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {step === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {working ? (
              <Box sx={{ py: 5, textAlign: "center" }}>
                <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>{file?.name}</Typography>
                <Typography sx={{ ...typography.bodyLarge, color: grayFontColor, mb: 2 }}>
                  {phase === "uploading" ? "Uploading…" : "Checking your file…"}
                </Typography>
                <LinearProgress aria-label="Checking your file" />
              </Box>
            ) : (
              <FileDropzone
                accept={ACCEPT}
                title="Drag your member file here"
                hint=".csv, .xlsx or .xls · up to 20,000 rows · 10 MB"
                error={fileError}
                onFileSelected={handleFile}
              />
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography sx={{ ...typography.bodyLarge, color: grayFontColor }}>
                Only a mobile number is required. Existing members are updated, never duplicated.
              </Typography>
              <Typography sx={{ ...typography.bodyLarge }}>Download template:</Typography>
              <Button size="small" onClick={() => downloadTemplate("xlsx")} sx={{ textTransform: "none" }}>
                Excel
              </Button>
              <Button size="small" onClick={() => downloadTemplate("csv")} sx={{ textTransform: "none" }}>
                CSV
              </Button>
            </Box>
          </Box>
        )}

        {step === 2 && job && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>{job.original_filename}</Typography>
              <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
                {job.total_rows} {job.total_rows === 1 ? "row" : "rows"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <StatTile value={counts.new ?? 0} label="New" valueColor={ColorsConstants.success} />
              <StatTile value={counts.update ?? 0} label="Will update" valueColor={ColorsConstants.primaryBlue90} />
              <StatTile value={counts.skipped ?? 0} label="Will be skipped" valueColor={counts.skipped ? ColorsConstants.error : undefined} />
              <StatTile value={counts.warnings ?? 0} label="Warnings" />
            </Box>

            {counts.cc_defaulted > 0 && (
              <Alert severity="info">
                {counts.cc_defaulted} {counts.cc_defaulted === 1 ? "row has" : "rows have"} no country code — 91 will be used.
              </Alert>
            )}
            {job.preview.new_classes?.length > 0 && (
              <Alert severity="info">
                New {job.preview.new_classes.length === 1 ? "class" : "classes"} will be created:{" "}
                {job.preview.new_classes.join(", ")}
              </Alert>
            )}

            {job.preview.issue_groups?.length > 0 && (
              <Box sx={{ border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`, borderRadius: "10px", p: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography component="h3" sx={{ ...typography.bodyLarge, fontWeight: 700 }}>
                    Issues found
                  </Typography>
                  {job.has_error_report && (
                    <Button size="small" onClick={downloadReport} sx={{ textTransform: "none" }}>
                      Download full report
                    </Button>
                  )}
                </Box>
                <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                  {job.preview.issue_groups.map((group) => (
                    <Box component="li" key={`${group.severity}-${group.code}`} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                      <AppChip
                        label={group.severity === "error" ? "Skipped" : "Warning"}
                        color={group.severity === "error" ? "error" : "warning"}
                        sx={{ minWidth: 72 }}
                      />
                      <Box>
                        <Typography sx={typography.bodyLarge}>
                          {group.examples[0]?.message}{" "}
                          <Typography component="span" sx={{ ...typography.bodySmall, color: grayFontColor }}>
                            ({group.count} {group.count === 1 ? "row" : "rows"})
                          </Typography>
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {job.preview.ignored_columns?.length > 0 && (
              <Typography sx={{ ...typography.bodyLarge, color: grayFontColor }}>
                Not imported: {job.preview.ignored_columns.join(", ")}
              </Typography>
            )}
            {toImport === 0 && (
              <Alert severity="warning">There is nothing to import: every row would be skipped.</Alert>
            )}
            {importError && (
              <Alert severity="error" role="alert">
                {importError}
              </Alert>
            )}
            {importing && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }} role="status">
                <CircularProgress size={22} aria-label="Importing" />
                <Typography sx={typography.bodyLarge}>
                  Importing members… large files can take up to a minute. Please keep this window open.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        {step === 2 ? (
          <>
            <ButtonOne variant="outlined" onClick={onClose} disabled={importing}>
              Cancel
            </ButtonOne>
            <ButtonOne onClick={handleImport} disabled={importing || toImport === 0}>
              {importing ? "Importing…" : `Import ${toImport} ${toImport === 1 ? "member" : "members"}`}
            </ButtonOne>
          </>
        ) : (
          <ButtonOne variant="outlined" onClick={onClose}>
            Cancel
          </ButtonOne>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UploadMembersModal;
